import React, { useState } from 'react';
import {
  SEVERITY_COLORS,
  SEVERITY_BG,
  SEVERITY_LABELS,
} from '../utils/symptomHelpers';

const COMMON_SYMPTOMS = [
  'Fatigue', 'Nausea', 'Vomiting', 'Pain', 'Peripheral neuropathy',
  'Appetite loss', 'Mouth sores', 'Constipation', 'Diarrhea',
  'Headache', 'Insomnia', 'Shortness of breath', 'Rash', 'Dizziness',
  'Chills', 'Fever', 'Hair loss', 'Muscle weakness', 'Swelling',
  'Anxiety', 'Cold sensitivity', 'Bone pain', 'Dry mouth',
];

const SEVERITIES = ['mild', 'moderate', 'severe', 'very_severe'];

function toDateTimeLocal(date) {
  const d = date || new Date();
  const pad = n => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

export default function SymptomForm({ onSave, onClose, saving }) {
  const [name, setName] = useState('');
  const [severity, setSeverity] = useState('mild');
  const [note, setNote] = useState('');
  const [loggedAt, setLoggedAt] = useState(toDateTimeLocal(new Date()));
  const [error, setError] = useState('');

  const handleSave = () => {
    if (!name.trim()) {
      setError('Please enter a symptom name.');
      return;
    }
    setError('');
    onSave({
      name: name.trim(),
      severity,
      note: note.trim(),
      loggedAt: new Date(loggedAt).toISOString(),
    });
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="symptom-form-title"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
      }}
      onClick={e => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '16px',
          padding: '28px',
          width: '100%',
          maxWidth: '460px',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 12px 40px rgba(0,0,0,0.2)',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '20px',
          }}
        >
          <h2
            id="symptom-form-title"
            style={{ fontSize: '20px', fontWeight: '700', color: '#1a1a1a', margin: 0 }}
          >
            Log Symptom
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              lineHeight: 1,
              cursor: 'pointer',
              color: '#718096',
              padding: '4px',
            }}
          >
            ×
          </button>
        </div>

        {/* Error */}
        {error && (
          <div
            style={{
              marginBottom: '16px',
              padding: '10px 12px',
              background: '#FCEBEB',
              border: '1px solid #FECACA',
              borderRadius: '8px',
              color: '#991B1B',
              fontSize: '14px',
            }}
          >
            {error}
          </div>
        )}

        {/* Symptom Name */}
        <div style={{ marginBottom: '18px' }}>
          <label
            htmlFor="symptom-name"
            style={{
              display: 'block',
              fontSize: '13px',
              fontWeight: '600',
              color: '#374151',
              marginBottom: '6px',
            }}
          >
            Symptom *
          </label>
          <input
            id="symptom-name"
            type="text"
            list="symptom-suggestions"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="e.g. Fatigue, Nausea…"
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '15px',
              fontFamily: 'inherit',
              boxSizing: 'border-box',
            }}
          />
          <datalist id="symptom-suggestions">
            {COMMON_SYMPTOMS.map(s => (
              <option key={s} value={s} />
            ))}
          </datalist>
        </div>

        {/* Severity */}
        <div style={{ marginBottom: '18px' }}>
          <span
            style={{
              display: 'block',
              fontSize: '13px',
              fontWeight: '600',
              color: '#374151',
              marginBottom: '8px',
            }}
          >
            Severity *
          </span>
          <div
            style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}
          >
            {SEVERITIES.map(s => (
              <button
                key={s}
                type="button"
                onClick={() => setSeverity(s)}
                style={{
                  padding: '10px 8px',
                  borderRadius: '8px',
                  border:
                    severity === s
                      ? `2px solid ${SEVERITY_COLORS[s]}`
                      : '1px solid #d1d5db',
                  background: severity === s ? SEVERITY_BG[s] : 'white',
                  color: severity === s ? SEVERITY_COLORS[s] : '#374151',
                  fontWeight: severity === s ? '600' : '400',
                  fontSize: '14px',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  transition: 'all 0.15s',
                }}
              >
                {SEVERITY_LABELS[s]}
              </button>
            ))}
          </div>
        </div>

        {/* Note */}
        <div style={{ marginBottom: '18px' }}>
          <label
            htmlFor="symptom-note"
            style={{
              display: 'block',
              fontSize: '13px',
              fontWeight: '600',
              color: '#374151',
              marginBottom: '6px',
            }}
          >
            Note (optional)
          </label>
          <textarea
            id="symptom-note"
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="Any additional details…"
            rows={3}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '15px',
              fontFamily: 'inherit',
              resize: 'vertical',
              boxSizing: 'border-box',
            }}
          />
        </div>

        {/* Date & Time */}
        <div style={{ marginBottom: '24px' }}>
          <label
            htmlFor="symptom-datetime"
            style={{
              display: 'block',
              fontSize: '13px',
              fontWeight: '600',
              color: '#374151',
              marginBottom: '6px',
            }}
          >
            Date &amp; Time
          </label>
          <input
            id="symptom-datetime"
            type="datetime-local"
            value={loggedAt}
            onChange={e => setLoggedAt(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '15px',
              fontFamily: 'inherit',
              boxSizing: 'border-box',
            }}
          />
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              flex: 1,
              padding: '12px',
              background: 'white',
              color: 'var(--teal)',
              border: '1px solid var(--teal)',
              borderRadius: '8px',
              fontSize: '15px',
              fontWeight: '600',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            style={{
              flex: 1,
              padding: '12px',
              background: saving ? '#9ca3af' : 'var(--teal)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '15px',
              fontWeight: '600',
              cursor: saving ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit',
            }}
          >
            {saving ? 'Saving…' : 'Save Symptom'}
          </button>
        </div>
      </div>
    </div>
  );
}
